import React from 'react';
import { Card, Flex, Radio, Typography, type CheckboxOptionType, theme } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import { CustomTooltip } from '../../components/Tooltip';
import { purifyHtml } from '~library/purifyHtml';

import useTrackCDNAnalyticsEvent from './useTrackCDNAnalyticsEvent';

type SelectedChartType = 'volume' | 'response-time';
const CRAWLER_VISITS_DESCRIPTION =
  'A HIT represents a requested page that was served from our cache. A MISS represents a requested page that was rendered on the fly, served, and only cached if it responded with a 200 status code.';

type CustomButtonLabelProps = {
  label: string;
  tooltip: string;
  selected?: boolean;
};
function CustomButtonLabel({ label, tooltip, selected }: CustomButtonLabelProps) {
  return (
    <Typography.Text
      style={{ color: selected ? 'white' : 'unset', fontWeight: '400', whiteSpace: 'nowrap' }}
    >
      {label}
      <CustomTooltip message={tooltip} placement="top">
        <InfoCircleOutlined style={{ marginLeft: 4 }} />
      </CustomTooltip>
    </Typography.Text>
  );
}

type CardHeaderProps = {
  selectedChartType: SelectedChartType;
  onChartTypeChange: (type: SelectedChartType) => void;
  descriptionVisible?: boolean;
};
function CardHeader({
  selectedChartType,
  onChartTypeChange,
  descriptionVisible,
}: CardHeaderProps) {
  const { token } = theme.useToken();
  const track = useTrackCDNAnalyticsEvent();

  const onChartChange = (type: SelectedChartType) => {
    track('ChartToggled', { chart_type: type });
    onChartTypeChange(type);
  };

  const CHARTS_AVAILABLE: CheckboxOptionType[] = [
    {
      label: (
        <CustomButtonLabel
          label="Volume"
          tooltip="Number of crawler visits."
          selected={selectedChartType === 'volume'}
        />
      ),
      value: 'volume',
    },
    {
      label: (
        <CustomButtonLabel
          label="Response Time"
          tooltip="Time to serve pages to crawlers. Hits come from cache, misses include rendering."
          selected={selectedChartType === 'response-time'}
        />
      ),
      value: 'response-time',
    },
  ];

  return (
    <Flex justify="space-between" align="flex-start" gap={token.marginSM} style={{ width: '100%' }}>
      <Flex vertical gap={token.marginXXS} style={{ flex: 1, minWidth: 0 }}>
        <div>
          <Typography.Text strong>Crawler visits</Typography.Text>

          {!descriptionVisible && (
            <CustomTooltip
              message={CRAWLER_VISITS_DESCRIPTION}
              placement="right"
            >
              <InfoCircleOutlined style={{ marginLeft: 4 }} />
            </CustomTooltip>
          )}
        </div>
        {descriptionVisible && (
          <Typography.Paragraph
            type="secondary"
            style={{
              fontSize: token.fontSizeSM,
              lineHeight: token.lineHeightSM,
              marginBottom: 0,
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              fontWeight: 'normal',
            }}
          >
            <span dangerouslySetInnerHTML={{ __html: purifyHtml(CRAWLER_VISITS_DESCRIPTION) }} />
          </Typography.Paragraph>
        )}
      </Flex>

      <Radio.Group
        size="small"
        style={{ flexShrink: 0 }}
        options={CHARTS_AVAILABLE}
        optionType="button"
        buttonStyle="solid"
        value={selectedChartType}
        onChange={(e) => onChartChange(e.target.value as SelectedChartType)}
      />
    </Flex>
  );
}

type CDNAnalyticsChartsLayoutProps = {
  VolumeChart: React.ReactElement;
  ResponseTimeChart: React.ReactElement;
  initialChartType?: SelectedChartType;
  descriptionVisible?: boolean;
};
function CDNAnalyticsChartsLayout({
  VolumeChart,
  ResponseTimeChart,
  initialChartType = 'volume',
  descriptionVisible = false,
}: CDNAnalyticsChartsLayoutProps) {
  const { token } = theme.useToken();
  const [chartType, setChartType] = React.useState<SelectedChartType>(initialChartType);

  return (
    <Card
      title={
        <CardHeader
          selectedChartType={chartType}
          onChartTypeChange={setChartType}
          descriptionVisible={descriptionVisible}
        />
      }
      styles={{
        header: descriptionVisible ? { paddingBlock: token.paddingMD } : undefined,
        title: descriptionVisible
          ? { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset' }
          : undefined,
        body: { paddingTop: token.padding, minHeight: 355 },
      }}
    >
      {chartType === 'volume' && VolumeChart}
      {chartType === 'response-time' && ResponseTimeChart}
    </Card>
  );
}

export default CDNAnalyticsChartsLayout;
